import React, { useRef, useEffect, useMemo, useState, useCallback } from "react";
import { observer } from "mobx-react-lite";
import styled from "@emotion/styled";
import { MDSSession } from "@/mds/components/sessions/session";
import {
  IMDSSessionProps,
  IMDSSessionRef,
  SessionEvent,
  SessionFeature,
  SessionStatus,
} from "@/mds/components/sessions/session.types";
import { useAppStore } from "@/store";
import { MemCommonMentionKind } from "@mem-labs/common-editor";
import { SessionObservable } from "@/store/sessions/SessionObservable";
import {
  MDSButtonGroup,
  MDSComboboxVariant,
  MDSIconButton,
  MDSTooltip,
  MDSTooltipPlacement,
  MDSSidePanelHeader,
  MDSSidePanelHeaderSlotName,
  MDSText,
  MDSTextVariant,
  MDSTextWeight,
  useTheme,
  MDSTextColor,
  MDSIconKind,
  MDSMenuDropdownSlotName,
  MDSActionMenuDropdown,
} from "@/mds";
import { FloatingNotePreview, ItemPreviewHoveringState } from "@/components/item-preview";
import { Maybe } from "@/domains/common/types";
import { INoteObservable } from "@/store/note";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import { SessionEventModelData } from "@/store/sessions/types";
import { MemItFeedbackModal } from "./feedback/mem-it-feedback-modal";
import EmptyOverlay from "./empty-overlay";
import { MDSComboboxMention } from "@/mds/components/forms/combobox/combobox.types";
import { SpaceAccountPostHogFeatureFlagChatZeroStateVariant } from "@/store/feature-flags/types";
import { checkIfIsOnboarding } from "./empty-overlay.utils";
import { TrackedEvent, trackEvent } from "@/domains/metrics";

interface MemItSidePanelViewProps {
  sessionId?: string;
  inSidePanel?: boolean;
  isClosed?: boolean;
  onClose?: () => void;
}

export const MemItActions = observer(() => {
  const { pageStore } = useAppStore();
  const memItStore = pageStore.memItPage;

  const hasPreviousSessions = useMemo(() => {
    return memItStore.previousChatSessions.length > 0;
  }, [memItStore.previousChatSessions]);

  const contentList = useMemo(() => {
    return memItStore.previousSessionsContentList;
  }, [memItStore.previousSessionsContentList]);

  const newSessionLabel = "New Chat";
  const previousSessionsLabel = "Past Chats";

  return (
    <>
      <MDSTooltip
        config={{
          label: newSessionLabel,
          placement: MDSTooltipPlacement.Top,
        }}
      >
        <MDSIconButton iconKind={MDSIconKind.Plus} onClick={memItStore.handleNewSession} />
      </MDSTooltip>
      {hasPreviousSessions && (
        <MDSTooltip config={{ label: previousSessionsLabel, placement: MDSTooltipPlacement.Top }}>
          <MDSActionMenuDropdown contentList={contentList}>
            <MDSIconButton slot={MDSMenuDropdownSlotName.Trigger} iconKind={MDSIconKind.History} />
          </MDSActionMenuDropdown>
        </MDSTooltip>
      )}
    </>
  );
});

export const MemItSidePanelView = observer<MemItSidePanelViewProps>(
  ({ sessionId, isClosed = false, onClose }) => {
    const { theme } = useTheme();
    const { pageStore } = useAppStore();
    const memItPageStore = pageStore.memItPage;

    useEffect(() => {
      // Explicitly initialize route mentions on mount (MobX reaction handles subsequent changes)
      memItPageStore.initializeCurrentObservableFromRoute();
    }, [memItPageStore]);

    useEffect(() => {
      // Sync the store's active session after render commit to avoid setState during render
      if (sessionId) {
        memItPageStore.setSessionId(sessionId);
      }
    }, [memItPageStore, sessionId]);

    useChatPanelViewTracking({ isClosed });

    return (
      <Container data-component="MemItSidePanelView" $isInteractive={isClosed}>
        <PanelHeaderContainer $isClosed={isClosed} $isInteractive={isClosed}>
          <MDSSidePanelHeader
            backgroundColor={theme.colors.surface.chat}
            bottomBorderColor={theme.colors.border.onAccent}
          >
            <MDSText
              slot={MDSSidePanelHeaderSlotName.Heading}
              variant={MDSTextVariant.TextS}
              weight={MDSTextWeight.Smedium}
              color={MDSTextColor.Contrast}
            >
              Chat
            </MDSText>
            {!isClosed && (
              <MDSButtonGroup
                slot={MDSSidePanelHeaderSlotName.Actions}
                data-test-id="mem-it-actions-group"
              >
                <MemItActions />
              </MDSButtonGroup>
            )}
          </MDSSidePanelHeader>
        </PanelHeaderContainer>
        {memItPageStore.session && (
          <Inner
            sessionObservable={memItPageStore.session}
            isClosed={isClosed}
            onClose={onClose}
            mentions={memItPageStore.currentMentions}
          />
        )}
      </Container>
    );
  }
);

const Inner = observer(
  ({
    sessionObservable,
    isClosed = false,
    mentions = [],
  }: {
    sessionObservable: SessionObservable;
    isClosed?: boolean;
    onClose?: () => void;
    mentions?: MDSComboboxMention[];
  }) => {
    const { store, pageStore } = useAppStore();
    const [hoverState] = useState(new ItemPreviewHoveringState());
    const [hoveredNote, setHoveredNote] = useState<Maybe<INoteObservable>>();
    const [hoveredText, setHoveredText] = useState<string | undefined>(undefined);
    const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
    const [isFeedbackPositive, setIsFeedbackPositive] = useState(false);
    const [currentFeedbackEventId, setCurrentFeedbackEventId] = useState<string | undefined>();
    const onLeftAddonButtonClick = useCallback(() => {
      if (!isClosed) return;
      store.sidePanel.goToMemIt(sessionObservable.id);
    }, [isClosed, store, sessionObservable.id]);

    const memItStore = pageStore.memItPage;

    const handleFeedback = useCallback(
      async (eventId: string, responseKind: "UPVOTE" | "DOWNVOTE") => {
        try {
          // Submit feedback immediately when user clicks
          memItStore.provideFeedback(eventId, responseKind);

          // Then show modal to collect additional details
          setCurrentFeedbackEventId(eventId);
          setIsFeedbackPositive(responseKind === "UPVOTE");
          setIsFeedbackModalOpen(true);
        } catch (error) {
          logger.error({
            message: "Failed to provide initial MemIt feedback",
            info: { error: objectModule.safeErrorAsJson(error as Error) },
          });
        }
      },
      [memItStore]
    );

    const handleCloseFeedbackModal = useCallback(() => {
      setIsFeedbackModalOpen(false);
      setCurrentFeedbackEventId(undefined);
    }, []);

    const handleSubmitFeedback = useCallback(
      async (suggestedFeedback: string | undefined, userText: string) => {
        if (!currentFeedbackEventId) return;

        try {
          const responseKind = isFeedbackPositive ? "UPVOTE" : "DOWNVOTE";
          const selectedSuggestions = suggestedFeedback ? [suggestedFeedback] : undefined;

          // Submit additional feedback details (this updates the existing feedback)
          await memItStore.provideFeedback(
            currentFeedbackEventId,
            responseKind,
            userText || undefined,
            selectedSuggestions
          );

          setIsFeedbackModalOpen(false);
          setCurrentFeedbackEventId(undefined);
        } catch (error) {
          logger.error({
            message: "Failed to provide additional MemIt feedback details",
            info: { error: objectModule.safeErrorAsJson(error as Error) },
          });
        }
      },
      [currentFeedbackEventId, isFeedbackPositive, memItStore]
    );

    const sessionRef = useRef<IMDSSessionRef>(null);
    const previousSessionIdRef = useRef<string | undefined>(undefined);
    const sessionData = sessionObservable.modelData;

    // If the current session is empty, and our panel is open, show the empty state.
    // If the pane is closed, we show the Session because it shows the chat message ui
    // at the bottom of the panel when another panel is open.
    // Don't show empty state if there's a pending new chat message about to be sent
    const showEmptyState =
      memItStore.sessionEvents.length === 0 && !isClosed && !memItStore.isPendingNewChat;

    // clear the pending-new-chat state since we're ready to show it
    useEffect(() => {
      if (memItStore.isPendingNewChat && memItStore.sessionEvents.length > 0) {
        setTimeout(() => {
          memItStore.clearIsPendingNewChat();
        }, 0);
      }
    }, [memItStore, showEmptyState]);

    // Swap the session when it changes
    useEffect(() => {
      const currentSessionId = sessionObservable.id;
      const previousSessionId = previousSessionIdRef.current;

      // Set the current session on the session context when it changes in
      // the observable which is held in the page store.
      if (previousSessionId !== currentSessionId) {
        sessionRef.current?.setCurrentSessionId(currentSessionId);

        // Update the ref
        previousSessionIdRef.current = currentSessionId;
      }
    }, [sessionObservable.id]);

    // Upsert the session events into the session context when they change on the observable.
    useEffect(() => {
      const sessionEvents = exportSessionEvents(memItStore.sessionEvents);
      sessionRef.current?.upsertEvents(sessionEvents);
    }, [memItStore.sessionEvents]);

    // Update the session status in the session context when it changes on the observable.
    useEffect(() => {
      if (sessionData?.status) {
        sessionRef.current?.updateStatus(sessionData.status as SessionStatus);
      }
    }, [sessionData?.status]);

    const linkHandler = (
      kind: MemCommonMentionKind,
      id: string,
      target: HTMLElement | null,
      event: React.MouseEvent<Element>,
      meta?: { noteVersion?: number | null }
    ) => {
      event.preventDefault();
      store.navigation.goToMention({
        id: id,
        mentionKind: kind,
        url: `${kind}/${id}`,
        highlightText: target?.textContent ?? undefined,
        keys: event,
        historyVersion: meta?.noteVersion ? meta.noteVersion : undefined,
        suppressSidePanelRouteHandler: true,
      });
    };

    const handleMemItemOver = useCallback(
      (itemKind: MemCommonMentionKind, itemId: string, target: HTMLElement | null) => {
        if (itemKind !== MemCommonMentionKind.Note) {
          return;
        }

        if (!target) {
          return;
        }

        hoverState.setHoveredRect(target?.getBoundingClientRect());
        setHoveredText(target?.innerText);
        setHoveredNote(store.notes.getNoteObservableById({ noteId: itemId }));
        hoverState.handleMouseEnter();
      },
      [store.notes, hoverState]
    );

    const handleMemItemOut = useCallback(() => {
      hoverState.setHoveredRect(null);
      setHoveredNote(undefined);
      hoverState.handleMouseLeave();
    }, [hoverState]);

    const sessionProps: IMDSSessionProps = {
      sessionTitle: "Mem It",
      sessionSubtitle: `Session: ${sessionObservable.id}`,
      initialSessionEvents: [],
      initialSessionStatus: (sessionData?.status as SessionStatus) || SessionStatus.Initial,
      containerHeight: 400, // Smaller height for sidebar
      isOpen: !isClosed,
      config: {
        features: [SessionFeature.ContextLimitExceededNewChatButton, SessionFeature.AllowCopy],
        openLinksInNewTab: false,
        linkHandler: linkHandler,
        onMemItemOver: handleMemItemOver,
        onMemItemOut: handleMemItemOut,
        feedbackHandler: handleFeedback,
      },
      comboboxProps: {
        placeholder: "Tell or ask Mem something",
        variant: MDSComboboxVariant.Brand,
        chatIsClosed: isClosed,
        fetchIsInstant: true,
        mentions: mentions,
        implicitMentions: memItStore.currentImplicitMentions,
        onMentionAdd: memItStore.handleMentionAdd,
        onMentionRemove: memItStore.handleMentionRemove,
        onMentionsClear: memItStore.handleMentionsClear,
        onLeftAddonButtonClick: onLeftAddonButtonClick,
      },
      handlers: {
        handleFetchComboboxData: memItStore.handleComboboxFetch,
        handleInputFormSubmit: memItStore.handleCreateUserMessage,
        handleNewSession: memItStore.handleNewSession,
        handleViewModeChanged: () => {},
        handleCountdownCompleted: () => {},
        handleRetry: async () => {
          // Clear existing session error before retry attempt
          sessionRef.current?.clearSessionError();
          await store.sessions.processSession(sessionObservable.id);
        },
      },
    };

    return (
      <>
        <SessionContainer data-component="SessionContainer">
          {showEmptyState && (
            <EmptyOverlay currentSessionId={sessionObservable.id} isClosed={isClosed} />
          )}
          <MDSSession ref={sessionRef} key={sessionObservable.id} {...sessionProps} />
        </SessionContainer>
        <MemItFeedbackModal
          isModalOpen={isFeedbackModalOpen}
          handleCloseModal={handleCloseFeedbackModal}
          isPositive={isFeedbackPositive}
          onSubmit={handleSubmitFeedback}
        />
        <FloatingNotePreview
          state={hoverState}
          noteObservable={hoveredNote}
          placement="right"
          highlightText={hoveredText}
        />
      </>
    );
  }
);

const Container = styled.div<{ $isInteractive: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;
  cursor: ${({ $isInteractive }) => ($isInteractive ? "pointer" : "default")};
  border-top-right-radius: ${({ theme }) => theme.borderRadius.xlarge};
  background-color: ${({ theme }) => theme.colors.surface.chat};
`;

const SessionContainer = styled.div`
  flex: 1;
  overflow: auto;
  background-color: ${({ theme }) => theme.colors.surface.chat};
`;

const exportSessionEvents = (sessionEvents: SessionEventModelData[]): SessionEvent[] => {
  return sessionEvents.map(event => ({
    id: event.id,
    session_id: event.session_id,
    locally_created_at: event.locally_created_at,
    internal_created_at: event.locally_created_at,
    internal_updated_at: event.locally_created_at,
    feedbackGiven: event.feedback_given,
    data: {
      kind: event.kind,
      value: event.value_json,
    } as SessionEvent["data"],
  }));
};

const useChatPanelViewTracking = ({ isClosed }: { isClosed: boolean }) => {
  const { store } = useAppStore();
  const isOnboarding = checkIfIsOnboarding({ store });
  const hasOverlayOpen = store.platformInfo.onboardingInfo.shouldShowWelcomeOverlay;

  useEffect(() => {
    // Track ChatPanelView only once per browser session when we open the panel
    // and no overlay is currently open
    if (isClosed) return;
    if (hasOverlayOpen) return;

    let variant = SpaceAccountPostHogFeatureFlagChatZeroStateVariant.AskAboutYourNotes;
    if (isOnboarding) {
      variant = SpaceAccountPostHogFeatureFlagChatZeroStateVariant.Onboarding;
    }

    logger.info({
      message: "[AppStoreTrackingStore]: Tracking chat_panel_view event",
      info: {
        variant,
      },
    });

    trackEvent(TrackedEvent.ChatPanelView, { variant });
  }, [isClosed, isOnboarding, hasOverlayOpen]);
};

const PanelHeaderContainer = styled.div<{ $isClosed: boolean; $isInteractive: boolean }>`
  position: relative;
  overflow: hidden;
  max-height: ${({ $isClosed }) => ($isClosed ? "0px" : "auto")};
  height: ${({ $isClosed }) => ($isClosed ? "0px" : "auto")};
  min-height: ${({ $isClosed }) => ($isClosed ? "0px" : "auto")};
  transition: height 0.16s ease;
`;
